<template>
  <div class="solutions-page">
    <!-- ========== 顶部导航 ========== -->
    <AppHeader />

    <!-- ========== 英雄区 ========== -->
    <section class="hero-section">
      <div class="hero-content">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          2026 解决方案矩阵
        </div>
        <h1 class="hero-title">
          行业智能<br />解决方案
        </h1>
        <p class="hero-desc">
          覆盖光伏、工业、仓储、城市等 12+ 行业场景，<br />
          提供从端到云的一站式智能物联方案
        </p>
        <div class="hero-actions">
          <el-button type="primary" size="large" round class="hero-btn-primary" @click="handleGetSolution">
            获取方案
            <el-icon><ArrowRight /></el-icon>
          </el-button>
          <el-button size="large" round class="hero-btn-ghost" @click="handleConsult">
            预约咨询
          </el-button>
        </div>
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-number">12+</span>
            <span class="stat-label">行业覆盖</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">5000+</span>
            <span class="stat-label">成功案例</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">98%</span>
            <span class="stat-label">客户满意度</span>
          </div>
        </div>
      </div>
      <div class="hero-visual">
        <div class="hero-orb"></div>
        <div class="hero-orb-secondary"></div>
        <div class="hero-grid-pattern"></div>
        <div class="hero-device-mockup">
          <el-icon :size="64"><Connection /></el-icon>
        </div>
      </div>
    </section>

    <!-- ========== 主体区域：搜索 + 左筛选 + 右展示 ========== -->
    <section class="main-section">
      <div class="main-inner">
        <!-- 搜索框（顶部） -->
        <div class="search-bar">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索解决方案名称、描述..."
            size="large"
            clearable
            @input="handleSearch"
            class="search-input"
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          <span class="search-result">
            共 <strong>{{ total }}</strong> 个方案
            <span v-if="hasActiveFilters">（已筛选）</span>
          </span>
        </div>

        <div class="content-wrapper">
          <!-- ====== 左侧筛选区 ====== -->
          <aside class="filter-sidebar">
            <div class="filter-header">
              <h4>筛选条件</h4>
              <el-button
                v-if="hasActiveFilters"
                type="text"
                size="small"
                @click="resetFilters"
                class="reset-btn"
              >
                <el-icon><Refresh /></el-icon> 重置全部
              </el-button>
            </div>

            <!-- 行业筛选（单选） -->
            <div class="filter-group">
              <div class="filter-group-header">
                <span class="filter-label">行业</span>
                <span class="filter-count">{{ selectedIndustryId ? 1 : 0 }}/{{ industryList.length }}</span>
              </div>
              <el-radio-group v-model="selectedIndustryId" @change="handleFilterChange" class="filter-radio-group">
                <el-radio
                  v-for="ind in industryList"
                  :key="ind.id"
                  :label="ind.id"
                  class="filter-radio"
                >
                  {{ ind.industryName }}
                </el-radio>
              </el-radio-group>
            </div>

            <!-- 已选标签 -->
            <div class="filter-tags" v-if="hasActiveFilters">
              <span class="filter-tags-label">已选：</span>
              <el-tag
                v-if="selectedIndustryId"
                size="small"
                closable
                @close="removeIndustry"
                class="filter-tag"
              >
                {{ getIndustryName(selectedIndustryId) }}
              </el-tag>
              <el-tag v-if="searchKeyword" size="small" closable @close="searchKeyword = ''; fetchSolutions(1, false)">
                关键词：{{ searchKeyword }}
              </el-tag>
            </div>
          </aside>

          <!-- ====== 右侧方案展示区 ====== -->
          <div class="solutions-content">
            <!-- 加载状态 -->
            <div v-if="loading" class="solutions-grid skeleton-grid">
              <div v-for="i in 6" :key="i" class="solution-card-skeleton">
                <div class="skeleton-image"></div>
                <div class="skeleton-text"></div>
                <div class="skeleton-text short"></div>
                <div class="skeleton-tags"></div>
              </div>
            </div>

            <!-- 方案网格 -->
            <div v-else-if="solutions.length > 0" class="solutions-grid">
              <div
                v-for="solution in solutions"
                :key="solution.id"
                class="solution-card"
                @click="goToDetail(solution.id)"
              >
                <div class="card-image-wrap">
                  <el-image
                    :src="solution.image"
                    fit="cover"
                    class="card-image"
                    lazy
                  >
                    <template #placeholder>
                      <div class="img-placeholder">
                        <el-icon :size="32"><Monitor /></el-icon>
                      </div>
                    </template>
                  </el-image>
                  <div class="card-overlay">
                    <span class="view-detail">查看方案 →</span>
                  </div>
                  <div class="card-badge" v-if="solution.tag">
                    <span>{{ solution.tag }}</span>
                  </div>
                </div>
                <div class="card-body">
                  <h3 class="card-title">{{ solution.name }}</h3>
                  <p class="card-desc">{{ solution.description }}</p>
                  <!-- 如果后端返回标签数组，可改为 v-for 遍历 -->
                  <!-- <div class="card-tags" v-if="solution.tag">
                    <span class="card-tag">{{ solution.tag }}</span>
                  </div> -->
                  <div class="card-footer">
                    <span class="card-industry">{{ getIndustryName(solution.industryId) }}</span>
                    <button class="card-compare" @click.stop="addCompare(solution.id)">
                      <el-icon><ScaleToOriginal /></el-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="empty-state">
              <el-icon :size="64"><Document /></el-icon>
              <h3>未找到匹配方案</h3>
              <p>请尝试调整筛选条件或关键词</p>
              <el-button type="primary" plain @click="resetFilters">重置筛选</el-button>
            </div>

            <!-- 加载更多 -->
            <div class="load-more-wrap" v-if="hasMore && !loading && solutions.length > 0">
              <button class="load-more-btn" @click="loadMore" :disabled="loadingMore">
                <span v-if="!loadingMore">加载更多</span>
                <span v-else><el-icon class="spin"><Loading /></el-icon> 加载中</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== 特色推荐区（保留静态） ========== -->
    <!-- <section class="featured-section">
      <div class="featured-inner">
        <div class="featured-info">
          <div class="featured-label">
            <span class="label-line"></span>
            精选方案
          </div>
          <h2>{{ featuredSolution?.name || '加载中...' }}</h2>
          <p>{{ featuredSolution?.desc || '' }}</p>
          <ul class="featured-features" v-if="featuredSolution?.features">
            <li v-for="(f, i) in featuredSolution.features" :key="i">
              <el-icon><Check /></el-icon> {{ f }}
            </li>
          </ul>
          <button class="featured-btn" @click="goToDetail(featuredSolution?.id)">
            了解详情
            <el-icon><ArrowRight /></el-icon>
          </button>
        </div>
        <div class="featured-visual">
          <div class="featured-glow"></div>
          <div class="featured-icon-wrap">
            <el-icon :size="72"><Cpu /></el-icon>
          </div>
        </div>
      </div>
    </section> -->
  </div>
</template>

<script setup>

import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Monitor, Connection, ArrowRight, Loading, Check, Cpu, ScaleToOriginal, Search, Refresh, Document
} from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import { getIndustries, getSolutionsPage } from '@/api/solution'
import { useRoute } from 'vue-router'
// 1. 在 import 中加入 watch
import { ref, computed, onMounted, watch } from 'vue'
const route = useRoute()
const router = useRouter()

// ---------- 状态 ----------
const searchKeyword = ref('')
const selectedIndustryId = ref(null)          // 单个行业ID
const industryList = ref([])                 // [{ id, industryName }]
const solutions = ref([])
const total = ref(0)
const currentPage = ref(1)
const pageSize = 6
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(true)



// 特色推荐（静态，可后续改为动态）
const featuredSolution = ref(null)

// ---------- 计算属性 ----------
const hasActiveFilters = computed(() => {
  return selectedIndustryId.value !== null || searchKeyword.value.trim().length > 0
})

// 根据ID获取行业名称
const getIndustryName = (id) => {
  const found = industryList.value.find(item => item.id === id)
  return found ? found.industryName : '未知行业'
}

// ---------- API 调用 ----------
// 获取行业列表
const fetchIndustries = async () => {
  try {
    const res = await getIndustries()
    industryList.value = res.data || []
    console.log('✅ 行业列表加载完成:', industryList.value.length)
  } catch (error) {
    console.error('获取行业列表失败', error)
    ElMessage.error('加载行业列表失败')
  }
}

const fetchSolutions = async (page = 1, append = false) => {
  if (append) {
    loadingMore.value = true
  } else {
    loading.value = true
    solutions.value = []
  }

  try {
    const params = {
      current: page,
      size: pageSize,
      keyword: searchKeyword.value || undefined,
      industryIds: selectedIndustryId.value !== null ? selectedIndustryId.value : undefined
    }
    const res = await getSolutionsPage(params)
    console.log('📦 原始响应:', res)

    // 分页接口直接返回 { records, total, ... }，其它接口返回 { code, data }
    const pageData = res.records !== undefined ? res : (res.data || {})
    console.log('📋 解析后 pageData:', pageData)

    const newRecords = pageData.records || []
    const totalCount = pageData.total || newRecords.length

    if (append) {
      solutions.value = [...solutions.value, ...newRecords]
    } else {
      solutions.value = newRecords
    }
    total.value = totalCount

    if (pageData.total > 0) {
      const pages = pageData.pages || Math.ceil(totalCount / pageSize)
      hasMore.value = page < pages
    } else {
      hasMore.value = newRecords.length === pageSize
    }
    currentPage.value = page

    console.log('✅ 当前方案数:', solutions.value.length)
  } catch (error) {
    console.error('获取方案列表失败', error)
    ElMessage.error('加载方案列表失败')
    if (!append) solutions.value = []
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

// 加载特色推荐（静态）
const loadFeatured = async () => {
  await new Promise(r => setTimeout(r, 300))
  featuredSolution.value = {
    id: 2,
    name: '智慧工厂数字孪生平台',
    desc: '5G+边缘计算打造工厂数字孪生，实现生产全流程可视化与智能调度',
    features: [
      '5G低时延实时数据采集',
      '边缘计算就近数据处理',
      '数字孪生可视化监控',
      'AI驱动的生产优化建议'
    ]
  }
}

// ---------- 事件处理 ----------
const handleSearch = () => {
  fetchSolutions(1, false)  // 搜索时重置到第一页
}

const handleFilterChange = () => {
  fetchSolutions(1, false)  // 行业变化时重置到第一页
}

const resetFilters = () => {
  searchKeyword.value = ''
  selectedIndustryId.value = null
  fetchSolutions(1, false)
  ElMessage.success('已重置所有筛选条件')
}

const removeIndustry = () => {
  selectedIndustryId.value = null
  fetchSolutions(1, false)
}

const loadMore = () => {
  if (loadingMore.value || !hasMore.value) return
  fetchSolutions(currentPage.value + 1, true)
}

const goToDetail = (id) => {
  if (id) router.push({ name: 'SolutionDetail', params: { id } })
}

const addCompare = (id) => {
  ElMessage.success(`已添加至对比列表`)
}

// 操作按钮
const handleGetSolution = () => {
  ElMessage.success('正在为您匹配最佳方案...')
}
const handleConsult = () => {
  ElMessage.success('已提交预约咨询，客服将尽快与您联系')
}

// ---------- 生命周期 ----------

// 2. 在 setup 中添加 watch
watch(
  () => route.query,
  (newQuery) => {
    // 处理 industryIds（只取第一个，因为后端是单选）
    const industryIdParam = newQuery.industryIds
    if (industryIdParam !== undefined) {
      const id = parseInt(industryIdParam, 10)
      if (!isNaN(id) && industryList.value.some(item => item.id === id)) {
        selectedIndustryId.value = id
      } else {
        selectedIndustryId.value = null
      }
    } else {
      selectedIndustryId.value = null
    }

    // 处理 keyword（可选）
    const keywordParam = newQuery.keyword
    if (keywordParam !== undefined) {
      searchKeyword.value = keywordParam || ''
    }
    // 如果只有 _t 参数，不改变 keyword

    // 重新加载方案（第一页）
    fetchSolutions(1, false)
  },
  { deep: true } // 不需要 immediate，由 onMounted 处理首次
)

// 3. 修改 onMounted
onMounted(async () => {
  await fetchIndustries() // 先加载行业列表

  // 处理首次加载的 URL 参数
  const industryIdParam = route.query.industryIds
  if (industryIdParam) {
    const id = parseInt(industryIdParam, 10)
    if (!isNaN(id) && industryList.value.some(item => item.id === id)) {
      selectedIndustryId.value = id
    }
  }
  const keywordParam = route.query.keyword
  if (keywordParam) {
    searchKeyword.value = keywordParam
  }

  await fetchSolutions(1, false) // 首次加载
  await loadFeatured()
})
</script>

<style scoped>
/* ========== 原有样式保持不变 ========== */
.solutions-page {
  padding-top: 72px;
  background: #f5f7fb;
  min-height: 100vh;
  font-family: -apple-system, 'SF Pro Display', 'PingFang SC', sans-serif;
}

/* ========== 英雄区 ========== */
.hero-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  min-height: 520px;
  position: relative;
}

.hero-content {
  flex: 1;
  z-index: 2;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(79, 70, 229, 0.08);
  color: #4F46E5;
  padding: 8px 20px 8px 14px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.3px;
  margin-bottom: 24px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4F46E5;
  animation: pulse-dot 2s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(0.8); }
}

.hero-title {
  font-size: 64px;
  font-weight: 700;
  line-height: 1.08;
  color: #0a0a1a;
  margin: 0 0 20px 0;
  letter-spacing: -2px;
}

.hero-desc {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.8;
  margin: 0 0 36px 0;
  max-width: 500px;
}

.hero-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 48px;
}

.hero-btn-primary {
  background: #0a0a1a !important;
  border: none !important;
  padding: 14px 40px !important;
  font-weight: 500;
  font-size: 16px !important;
  transition: all 0.3s ease;
}

.hero-btn-primary:hover {
  background: #2a2a4a !important;
  transform: translateY(-2px);
  box-shadow: 0 12px 32px rgba(10, 10, 26, 0.2);
}

.hero-btn-ghost {
  border: 1.5px solid #d1d5db !important;
  background: transparent !important;
  color: #1a1a2e !important;
  padding: 14px 40px !important;
  font-weight: 500;
  font-size: 16px !important;
  transition: all 0.3s ease;
}

.hero-btn-ghost:hover {
  border-color: #0a0a1a !important;
  background: rgba(10, 10, 26, 0.04) !important;
  transform: translateY(-2px);
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #0a0a1a;
  letter-spacing: -0.5px;
}

.stat-label {
  font-size: 14px;
  color: #9ca3af;
  margin-top: 2px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: #e5e7eb;
}

.hero-visual {
  flex: 0 0 400px;
  height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-orb {
  position: absolute;
  width: 360px;
  height: 360px;
  border-radius: 50%;
  background: radial-gradient(circle at 30% 30%, rgba(79, 70, 229, 0.15), rgba(79, 70, 229, 0.02));
  animation: float-orb 8s ease-in-out infinite;
}

.hero-orb-secondary {
  position: absolute;
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: radial-gradient(circle at 70% 70%, rgba(16, 185, 129, 0.1), transparent);
  animation: float-orb 10s ease-in-out infinite reverse;
}

@keyframes float-orb {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-12px, -20px) scale(1.05); }
}

.hero-grid-pattern {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(79, 70, 229, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(79, 70, 229, 0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  border-radius: 50%;
}

.hero-device-mockup {
  position: relative;
  z-index: 2;
  width: 140px;
  height: 140px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 20px 60px rgba(10, 10, 26, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4F46E5;
}

/* ========== 主体区域 ========== */
.main-section {
  max-width: 75%;
  margin: 0 auto;
  padding: 0 64px 24px;
}

.main-inner {
  background: #ffffff;
  border-radius: 24px;
  padding: 32px 36px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

/* ---- 搜索栏 ---- */
.search-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid #f0f2f5;
}

.search-input {
  flex: 1;
}

.search-input :deep(.el-input__wrapper) {
  border-radius: 14px;
  padding: 6px 20px;
  background: #f5f7fb;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.search-input :deep(.el-input__wrapper:hover) {
  border-color: #d1d5db;
  background: #fff;
}

.search-input :deep(.el-input__wrapper.is-focus) {
  border-color: #4F46E5;
  box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.08);
  background: #fff;
}

.search-input :deep(.el-input__inner) {
  font-size: 15px;
  color: #1a1a2e;
}

.search-result {
  font-size: 15px;
  color: #6b7280;
  white-space: nowrap;
}

.search-result strong {
  color: #0a0a1a;
  font-weight: 700;
}

/* ---- 内容包装器：左筛 + 右展 ---- */
.content-wrapper {
  display: flex;
  gap: 40px;
  align-items: flex-start;
}

/* ====== 左侧筛选侧栏 ====== */
.filter-sidebar {
  flex: 0 0 260px;
  position: sticky;
  top: 92px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  padding-right: 12px;
}

.filter-sidebar::-webkit-scrollbar {
  width: 4px;
}

.filter-sidebar::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 4px;
}

.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.filter-header h4 {
  font-size: 17px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0;
}

.reset-btn {
  color: #6b7280 !important;
  font-size: 13px;
  padding: 0 !important;
}

.reset-btn:hover {
  color: #4F46E5 !important;
}

.filter-group {
  margin-bottom: 28px;
}

.filter-group:last-of-type {
  margin-bottom: 18px;
}

.filter-group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.filter-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.filter-count {
  font-size: 13px;
  color: #9ca3af;
}

/* ---- 单选行业样式（覆盖原生样式） ---- */
.filter-radio-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-radio {
  display: block !important;
  margin: 0 !important;
}
.filter-radio :deep(.el-radio__input) {
  display: none;
}
.filter-radio :deep(.el-radio__label) {
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  transition: all 0.2s ease;
  cursor: pointer;
  display: block;
  width: 100%;
  font-weight: 400;
}
.filter-radio :deep(.el-radio__label:hover) {
  background: #f3f4f6;
  color: #1a1a2e;
}
.filter-radio.is-checked :deep(.el-radio__label) {
  background: rgba(79, 70, 229, 0.08);
  color: #4F46E5;
  font-weight: 500;
}

/* 已选标签 */
.filter-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}

.filter-tags-label {
  font-size: 13px;
  color: #9ca3af;
  margin-right: 4px;
}

.filter-tag {
  border-radius: 6px;
  font-size: 13px;
}

.filter-tag :deep(.el-tag__close) {
  font-size: 11px;
}

/* ====== 右侧方案展示 ====== */
.solutions-content {
  flex: 1;
  min-width: 0;
}

.solutions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

/* ---- 骨架屏 ---- */
.skeleton-grid {
  gap: 24px;
}

.solution-card-skeleton {
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #f0f2f5;
}

.skeleton-image {
  width: 100%;
  padding-top: 60%;
  background: #eef2f6;
  animation: shimmer 1.5s ease-in-out infinite;
}

.skeleton-text {
  height: 16px;
  background: #eef2f6;
  margin: 16px 20px 0;
  border-radius: 4px;
  animation: shimmer 1.5s ease-in-out infinite;
}

.skeleton-text.short {
  width: 60%;
  margin-top: 10px;
}

.skeleton-tags {
  height: 26px;
  background: #eef2f6;
  margin: 14px 20px 20px;
  border-radius: 4px;
  width: 40%;
  animation: shimmer 1.5s ease-in-out infinite;
}

@keyframes shimmer {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* ---- 方案卡片 ---- */
.solution-card {
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  cursor: pointer;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
}

.solution-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 56px rgba(10, 10, 26, 0.08);
  border-color: rgba(79, 70, 229, 0.15);
}

.card-image-wrap {
  position: relative;
  width: 100%;
  padding-top: 60%;
  overflow: hidden;
  background: #f5f7fb;
}

.card-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.6s ease;
}

.solution-card:hover .card-image {
  transform: scale(1.04);
}

.img-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f6;
  color: #b0b8c4;
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 10, 26, 0.3);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.35s ease;
}

.solution-card:hover .card-overlay {
  opacity: 1;
}

.view-detail {
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0.5px;
  transform: translateY(10px);
  transition: transform 0.35s ease;
}

.solution-card:hover .view-detail {
  transform: translateY(0);
}

.card-badge {
  position: absolute;
  top: 14px;
  left: 14px;
  background: rgba(10, 10, 26, 0.85);
  backdrop-filter: blur(8px);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 14px;
  border-radius: 100px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.card-body {
  padding: 20px 22px 22px;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 6px 0;
  line-height: 1.4;
}

.card-desc {
  font-size: 13px;
  color: #8b95a9;
  margin: 0 0 12px 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-tags {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.card-tag {
  font-size: 11px;
  font-weight: 500;
  color: #4F46E5;
  background: rgba(79, 70, 229, 0.08);
  padding: 3px 12px;
  border-radius: 100px;
  letter-spacing: 0.2px;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #f0f2f5;
  padding-top: 14px;
}

.card-industry {
  font-size: 12px;
  color: #a0aab8;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.card-compare {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: none;
  background: #f5f7fb;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  font-size: 16px;
}

.card-compare:hover {
  background: #4F46E5;
  color: #fff;
  transform: scale(1.05);
}

/* ---- 空状态 ---- */
.empty-state {
  text-align: center;
  padding: 64px 24px;
  background: #fafbfc;
  border-radius: 18px;
  border: 1px solid #f0f2f5;
}

.empty-state .el-icon {
  color: #d1d5db;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 19px;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #9ca3af;
  margin: 0 0 20px 0;
  font-size: 15px;
}

/* ---- 加载更多 ---- */
.load-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 36px;
}

.load-more-btn {
  padding: 12px 48px;
  border-radius: 100px;
  border: 1.5px solid #e5e7eb;
  background: transparent;
  color: #1a1a2e;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
}

.load-more-btn:hover:not(:disabled) {
  border-color: #0a0a1a;
  background: rgba(10, 10, 26, 0.04);
}

.load-more-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ========== 特色推荐区 ========== */
.featured-section {
  max-width: 1440px;
  margin: 28px auto 0;
  padding: 0 64px 72px;
}

.featured-inner {
  background: #0a0a1a;
  border-radius: 32px;
  padding: 56px 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 48px;
  overflow: hidden;
  position: relative;
}

.featured-info {
  flex: 1;
  color: #fff;
  z-index: 2;
}

.featured-label {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 14px;
}

.label-line {
  width: 28px;
  height: 2px;
  background: #4F46E5;
  border-radius: 2px;
}

.featured-info h2 {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 10px 0;
  letter-spacing: -0.5px;
}

.featured-info p {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.8;
  margin: 0 0 18px 0;
}

.featured-features {
  list-style: none;
  padding: 0;
  margin: 0 0 28px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
}

.featured-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.featured-features li .el-icon {
  color: #34d399;
  font-size: 16px;
}

.featured-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 32px;
  border-radius: 100px;
  border: none;
  background: #fff;
  color: #0a0a1a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
}

.featured-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.15);
}

.featured-visual {
  flex: 0 0 180px;
  height: 180px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.featured-glow {
  position: absolute;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: radial-gradient(circle at center, rgba(79, 70, 229, 0.2), transparent 70%);
  animation: pulse-glow 4s ease-in-out infinite;
}

@keyframes pulse-glow {
  0%, 100% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.2); opacity: 1; }
}

.featured-icon-wrap {
  position: relative;
  z-index: 2;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.15);
}

/* ========== 响应式 ========== */
@media (max-width: 1200px) {
  .hero-section { padding: 60px 48px; }
  .hero-title { font-size: 48px; }
  .hero-visual { flex: 0 0 320px; height: 320px; }
  .hero-orb { width: 280px; height: 280px; }
  .hero-orb-secondary { width: 180px; height: 180px; }
  .filter-sidebar { flex: 0 0 220px; }
  .solutions-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); }
}

@media (max-width: 1024px) {
  .main-section { padding: 0 32px 16px; }
  .main-inner { padding: 24px 24px; }
  .featured-section { padding: 0 32px 48px; }
  .featured-inner { padding: 40px 40px; }
}

@media (max-width: 820px) {
  .hero-section {
    flex-direction: column;
    padding: 40px 24px 32px;
    text-align: center;
    gap: 36px;
  }
  .hero-desc { max-width: 100%; }
  .hero-actions { justify-content: center; }
  .hero-stats { justify-content: center; }
  .hero-visual { flex: 0 0 240px; height: 240px; }
  .hero-orb { width: 200px; height: 200px; }
  .hero-title { font-size: 38px; }

  .main-section { padding: 0 16px 12px; }
  .main-inner { padding: 18px 16px; }

  .search-bar { flex-direction: column; align-items: stretch; gap: 12px; }
  .search-result { text-align: right; font-size: 14px; }

  .content-wrapper { flex-direction: column; gap: 28px; }
  
  .filter-sidebar {
    flex: none;
    width: 100%;
    position: static;
    max-height: none;
    overflow-y: visible;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 28px;
    padding-right: 0;
  }
  .filter-header { grid-column: 1 / -1; margin-bottom: 0; }
  .filter-group { margin-bottom: 0; }
  .filter-group:last-of-type { margin-bottom: 0; }
  .filter-tags { grid-column: 1 / -1; }

  .solutions-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 18px; }

  .featured-inner {
    flex-direction: column;
    padding: 32px 28px;
    text-align: center;
  }
  .featured-features { justify-content: center; }
  .featured-btn { justify-content: center; }
  .featured-visual { flex: 0 0 140px; height: 140px; }
  .featured-section { padding: 0 16px 40px; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 30px; }
  .hero-section { padding: 24px 16px 20px; min-height: auto; }
  .hero-actions { flex-direction: column; align-items: center; }
  .hero-stats { flex-wrap: wrap; gap: 12px; }
  .stat-divider { display: none; }
  .hero-visual { flex: 0 0 160px; height: 160px; }
  .hero-device-mockup { width: 90px; height: 90px; }

  .main-section { padding: 0 12px 8px; }
  .main-inner { padding: 14px 12px; border-radius: 16px; }

  .filter-sidebar { grid-template-columns: 1fr; gap: 12px; }

  .solutions-grid { grid-template-columns: 1fr 1fr; gap: 12px; }
  .card-body { padding: 14px 14px 16px; }
  .card-title { font-size: 13px; }
  .card-desc { font-size: 12px; }
  .card-badge { font-size: 10px; padding: 3px 10px; top: 10px; left: 10px; }
  .card-tag { font-size: 10px; padding: 2px 10px; }

  .featured-inner { padding: 24px 16px; border-radius: 20px; }
  .featured-info h2 { font-size: 22px; }
  .featured-section { padding: 0 12px 32px; }
  .load-more-btn { padding: 10px 28px; font-size: 13px; }
}
</style>